import React, { useCallback, useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  VStack,
} from "@chakra-ui/react";
import {
  getBranches,
  getContacts,
  getCostCenters,
  getProjects,
} from "../../../../Api/apiClient";
import { getEndOfMonth, getStartOfPeriod } from "../utils/dateUtils";
import { ReportActionButton } from "../../Shared/Components/ReportActionButton";
import { ReportFilterButton } from "../../Shared/Components/ReportFilterButton";
import { reportToolbarStyles } from "../../Shared/Styles/reportToolbarStyles";
import FilterMultiSelect from "./FilterMultiSelect";

export default function CashFlowMainFilterPopover({
  initialFilters,
  onApplyFilters,
  texts,
}) {
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState(initialFilters.branches || []);
  const [selectedProjects, setSelectedProjects] = useState(initialFilters.projects || []);
  const [selectedCostCenters, setSelectedCostCenters] = useState(initialFilters.costCenters || []);
  const [selectedContacts, setSelectedContacts] = useState(initialFilters.contacts || []);
  const [periodCount, setPeriodCount] = useState(initialFilters.period_count || 0);
  const [groupBy, setGroupBy] = useState(initialFilters.group_by || "");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setSelectedBranches(initialFilters.branches || []);
    setSelectedProjects(initialFilters.projects || []);
    setSelectedCostCenters(initialFilters.costCenters || []);
    setSelectedContacts(initialFilters.contacts || []);
    setPeriodCount(initialFilters.period_count || 0);
    setGroupBy(initialFilters.group_by || "");
  }, [initialFilters]);

  const loadOptions = useCallback(async () => {
    if (isLoaded) return;

    const [branchesRes, projectsRes, costCentersRes, contactsRes] = await Promise.all([
      getBranches(),
      getProjects(),
      getCostCenters(),
      getContacts(),
    ]);

    setBranches(branchesRes.data.results || []);
    setProjects(projectsRes.data.results || []);
    setCostCenters(costCentersRes.data.results || []);
    setContacts(contactsRes.data.results || []);
    setIsLoaded(true);
  }, [isLoaded]);

  const handleApply = () => {
    onApplyFilters({
      ...initialFilters,
      period_count: periodCount,
      group_by: groupBy,
      branches: selectedBranches,
      projects: selectedProjects,
      costCenters: selectedCostCenters,
      contacts: selectedContacts,
      date_before: getEndOfMonth(initialFilters.date_before, groupBy || "month"),
      date_after: getStartOfPeriod(initialFilters.date_before, groupBy || "month"),
    });
  };

  const handleClear = () => {
    const clearedDate = getEndOfMonth(new Date().toISOString().split("T")[0]);
    onApplyFilters({
      ...initialFilters,
      date_before: clearedDate,
      date_after: getStartOfPeriod(clearedDate, "month"),
      period_count: 0,
      group_by: "",
      branches: [],
      projects: [],
      costCenters: [],
      contacts: [],
    });
  };

  return (
    <Popover placement="bottom-start" isLazy onOpen={loadOptions}>
      <PopoverTrigger>
        <ReportFilterButton>
          {texts.reports?.filter || "Filter"}
        </ReportFilterButton>
      </PopoverTrigger>
      <PopoverContent {...reportToolbarStyles.popoverContent} w="320px" zIndex={1500}>
        <PopoverArrow />
        <PopoverHeader fontWeight="bold" p={2}>
          {texts.reports?.filter || "Filter"}
        </PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody p={2}>
          <VStack as="div" spacing={1} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs">{texts.reports?.comparePeriod || "Compare Period"}</FormLabel>
              <Select size="xs" value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                <option value="">{texts.reports?.noComparison || "No Comparison"}</option>
                <option value="month">{texts.reports?.month || "Month"}</option>
                <option value="year">{texts.reports?.year || "Year"}</option>
              </Select>
            </FormControl>
            {branches.length > 0 && (
              <FilterMultiSelect
                label={texts.reports?.branch || "Branches"}
                options={branches}
                selectedValues={selectedBranches}
                onSelectionChange={setSelectedBranches}
                texts={texts}
              />
            )}
            {projects.length > 0 && (
              <FilterMultiSelect
                label={texts.reports?.project || "Projects"}
                options={projects}
                selectedValues={selectedProjects}
                onSelectionChange={setSelectedProjects}
                texts={texts}
              />
            )}
            {costCenters.length > 0 && (
              <FilterMultiSelect
                label={texts.reports?.costCenter || "Cost Centers"}
                options={costCenters}
                selectedValues={selectedCostCenters}
                onSelectionChange={setSelectedCostCenters}
                texts={texts}
              />
            )}
            {contacts.length > 0 && (
              <FilterMultiSelect
                label={texts.reports?.contact || "Contacts"}
                options={contacts}
                selectedValues={selectedContacts}
                onSelectionChange={setSelectedContacts}
                texts={texts}
              />
            )}
            <HStack justify="space-between" mt={2}>
              <ReportActionButton variant="ghost" onClick={handleClear}>
                {texts.reports?.clearFilters || "Clear Filters"}
              </ReportActionButton>
              <ReportActionButton variant="primary" onClick={handleApply}>
                {texts.reports?.applyFilters || "Apply Filters"}
              </ReportActionButton>
            </HStack>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
